/**
 * ThomLogo — the single source of truth for the THOM VERIFY™ corporate mark.
 *
 * Layout: [TV monogram]  THOM VERIFY™
 *                        Securing Assets. Building Trust.
 *
 * Variants:
 *   `size`         xs (24) · sm (32) · md (40 — default) · lg (56) · xl (96)
 *   `stacked`      true → monogram above wordmark (login / splash / footer center)
 *   `showTagline`  true → renders the tagline underneath (default true)
 *   `variant`      "auto" (default) — dark text on light bg / light text on dark bg
 *                  "onDark" — force light text
 *                  "onLight" — force dark text
 *   `showTm`       show trademark symbol (default true)
 *
 * The monogram asset is served from `/brand/thom-tv-icon.png` (transparent PNG
 * cropped to the TV letters) so it composites cleanly on any background.
 */
const ICON = "/brand/thom-tv-icon.png";
const SIZES = { xs: 24, sm: 32, md: 40, lg: 56, xl: 96 };

export const ThomLogo = ({
  size = "md",
  stacked = false,
  showTagline = true,
  variant = "auto",
  showTm = true,
  className = "",
  "data-testid": testId = "thom-logo",
}) => {
  const px = typeof size === "number" ? size : (SIZES[size] || SIZES.md);
  const wordSize = px < 32 ? "text-sm" : px < 44 ? "text-lg" : px < 64 ? "text-2xl" : "text-4xl";
  const taglineSize = px < 32 ? "text-[9px]" : px < 44 ? "text-[10px]" : "text-xs";

  const colorText   = variant === "onDark" ? "text-white" : variant === "onLight" ? "text-[#0A0A0A]" : "text-foreground";
  const colorMuted  = variant === "onDark" ? "text-white/70" : variant === "onLight" ? "text-neutral-500" : "text-muted-foreground";

  const monogram = (
    <img
      src={ICON}
      alt=""
      role="presentation"
      width={px}
      height={px}
      draggable={false}
      className="block select-none shrink-0"
      style={{ width: px, height: px, aspectRatio: "1 / 1", objectFit: "contain" }}
    />
  );

  const wordmark = (
    <div className={`leading-none ${stacked ? "text-center" : ""}`}>
      <div
        className={`${wordSize} ${colorText}`}
        style={{
          fontFamily: '"Cabinet Grotesk", "Cormorant Garamond", "IBM Plex Serif", "Times New Roman", serif',
          letterSpacing: "0.22em",
          fontWeight: 500,
        }}
      >
        THOM VERIFY{showTm && <sup className="text-[0.55em] ml-0.5 opacity-70">™</sup>}
      </div>
      {showTagline && (
        <div
          className={`${taglineSize} ${colorMuted} mt-1.5`}
          style={{
            fontFamily: '"Cabinet Grotesk", ui-sans-serif, system-ui',
            letterSpacing: "0.18em",
            fontWeight: 300,
            textTransform: "uppercase",
          }}
        >
          Securing Assets · Building Trust
        </div>
      )}
    </div>
  );

  if (stacked) {
    return (
      <div
        className={`inline-flex flex-col items-center gap-3 ${className}`}
        data-testid={testId}
        aria-label="THOM Verify — Securing Assets, Building Trust"
      >
        {monogram}
        {wordmark}
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-3 ${className}`}
      data-testid={testId}
      aria-label="THOM Verify — Securing Assets, Building Trust"
    >
      {monogram}
      {wordmark}
    </div>
  );
};

export default ThomLogo;
